import React, { CSSProperties } from "react";
import { Box, CircularProgress, Typography, styled } from "@mui/material";

export type DimmerLoaderProps = {
  active: boolean;
  text?: string;
  inverted?: boolean; // Light background like Semantic's inverted
  size?: "small" | "medium" | "large";
  fullScreen?: boolean; // Cover entire screen vs container
  styles?: CSSProperties;
};

// Styled Components
const DimmerOverlay = styled(Box, {
  shouldForwardProp: (prop) =>
    !["active", "inverted", "fullScreen"].includes(prop as string),
})<{ active: boolean; inverted?: boolean; fullScreen?: boolean }>(
  ({ active, inverted, fullScreen }) => ({
    position: fullScreen ? "fixed" : "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: inverted
      ? "rgba(255, 255, 255, 0.85)"
      : "rgba(0, 0, 0, 0.6)",
    zIndex: fullScreen ? 9999 : 1000,
    opacity: active ? 1 : 0,
    visibility: active ? "visible" : "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
    pointerEvents: active ? "auto" : "none",
  })
);

const LoaderContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "1rem",
});

const LoaderText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "inverted",
})<{ inverted?: boolean }>(({ inverted }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: inverted ? "#333333" : "white",
  textAlign: "center",
  marginTop: "0.5rem",
}));

const getLoaderSize = (size: DimmerLoaderProps["size"]) => {
  switch (size) {
    case "small":
      return 24;
    case "large":
      return 56;
    case "medium":
    default:
      return 40;
  }
};

const DimmerLoader: React.FC<DimmerLoaderProps> = ({
  active,
  text,
  inverted = true, // Default to light background like Semantic
  size = "medium",
  fullScreen = false,
  styles = undefined,
}) => {
  return (
    <DimmerOverlay id="megen-dimmer-overlay" active={active} inverted={inverted} fullScreen={fullScreen} sx={styles}>
      <LoaderContainer id="megen-dimmer-loader-container">
        <CircularProgress
          id="megen-dimmer-loader-spinner"
          size={getLoaderSize(size)}
          thickness={4}
          sx={{
            color: inverted ? "var(--primary-theme-color)" : "white",
          }}
        />
        {text && <LoaderText id="megen-dimmer-loader-text" inverted={inverted}>{text}</LoaderText>}
      </LoaderContainer>
    </DimmerOverlay>
  );
};

export default DimmerLoader;